import {
  CommandBar,
  mergeStyles,
  MessageBar,
  MessageBarType,
  Stack,
} from "@fluentui/react";
import { styles } from "./experimentsTable.styles";
import ExperimentsTableConfigPipelineBanner from "./ExperimentsTableConfigPipelineBanner/ExperimentsTableConfigPipelineBanner";
import ExtensionsComboBox from "./ExtensionsCombox";
import {
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
} from "@fluentui/react-components";
import { useFetchExtensionConfigOpenPrs } from "../Hooks/AdoHooks/UseFetchBaseJsonConfig";
import { useFetchPipelineConfig } from "../Hooks/AdoHooks/UseFetchPipelineConfig";
import { useState } from "react";
import { useGetFeatureParam } from "../Hooks/UseFeature";
import { AcceptedFeatures } from "../Shared/shared.consts";
import { useAppContext } from "../Contexts";
import ArchiveTable from "./ArchiveTable/ArchiveTable";
import useAppInsights from "../Hooks/UseAppInsights";
import { useFetchExperiments } from "../Hooks/AdoHooks/UseFetchExperiments";
import ExperimentsTable from "./ExperimentsTable";
import { NavType } from "../Shared/AppInsights";
import { useNavigateEv2Experiments } from "../Hooks/UseNavigateToEv2Experiments";
import { useDataAccessContext } from "../Contexts/DataAccessContext";

interface ExperimentsTablePageProps {
  /**
   * When true will direct users to select an extension.
   */
  isRoot?: boolean;
}

const EXPERIMENTS_KEY = "experiments";
const ARCHIVED_KEY = "archived";

/**
 * Page component for displaying experiments in a table with tabs for active and archived experiments, and a command bar for actions like creating new experiments or viewing extension details
 */
const ExperimentsTablePage: React.FC<ExperimentsTablePageProps> = ({
  isRoot,
}) => {
  const { extensions, extensionName, setError } = useAppContext();
  const { hasAdoAccess } = useDataAccessContext();

  const { trackNavigationEvent, trackOperationEvent } = useAppInsights(
    "ExperimentsTablePage",
  );
  const [selectedTab, setSelectedTab] = useState<string>(EXPERIMENTS_KEY);
  const hideTopBar = useGetFeatureParam(AcceptedFeatures.hideTopBar.key);
  const { value: pipelineConfig, loading: pipelineConfigLoading } =
    useFetchPipelineConfig(setError);
  const { value: baseExtensionConfigOpenPr } = useFetchExtensionConfigOpenPrs();

  const {
    value: { experiments = [], archivedExperiments = [] } = {
      experiments: [],
      archivedExperiments: [],
    },
    loading: loadingExperiments,
    refresh,
  } = useFetchExperiments(setError);

  const {
    navigateToExperiment: navigateExperiment,
    navigateToExtensionDetails,
  } = useNavigateEv2Experiments();

  const onTabSelected = (_event: SelectTabEvent, data: SelectTabData) => {
    const selected = data.value as string;
    setSelectedTab(selected);
    trackOperationEvent({
      operation: "TabChange",
      function: "onTabSelected",
      properties: { selectedTab: selected },
    });
  };

  const handleCreate = () => {
    const destination = navigateExperiment(extensionName, undefined, {
      isCreate: true,
    });
    trackNavigationEvent({
      destination: destination,
      navType: NavType.Internal,
      function: "handleCreate",
    });
  };

  const handleViewExtensionDetails = () => {
    const destination = navigateToExtensionDetails(extensionName);
    trackNavigationEvent({
      destination: destination,
      navType: NavType.Internal,
      function: "handleViewExtensionDetails",
    });
  };

  return (
    <Stack className={styles.experimentsTablePageContainer}>
      {baseExtensionConfigOpenPr && (
        <MessageBar messageBarType={MessageBarType.warning}>
          There is an open pull request to update base extension configurations.
          These changes will affect all experiments, please open{" "}
          <em>Extension Details</em> to review changes.
        </MessageBar>
      )}
      <Stack.Item>
        {!pipelineConfigLoading && !pipelineConfig && (
          <ExperimentsTableConfigPipelineBanner />
        )}
      </Stack.Item>
      <Stack
        horizontal
        horizontalAlign="center"
        className={mergeStyles(
          styles.commandBarContainerStyles,
          !!hideTopBar && styles.commandBarContainerHideTopBar,
        )}
        tokens={{ childrenGap: 10 }}
      >
        <Stack.Item className={styles.extensionComboBoxContainer}>
          {hideTopBar && (
            <ExtensionsComboBox
              extensions={extensions}
              selected={extensionName}
            />
          )}
        </Stack.Item>
        <Stack.Item grow>
          <CommandBar
            styles={{
              root: styles.commandBarItemStyles,
            }}
            items={[
              {
                key: "createExperiment",
                text: "Create Experiment",
                iconProps: { iconName: "Add" },
                onClick: handleCreate,
                styles: {
                  root: styles.commandBarItemStyles,
                },
                disabled: !extensionName || !hasAdoAccess,
                ariaLabel: "Create Experiment",
              },
              {
                key: "extensionDetails",
                text: "Extension Details",
                iconProps: { iconName: "EntryView" },
                onClick: handleViewExtensionDetails,
                styles: {
                  root: styles.commandBarItemStyles,
                },
                disabled: !extensionName,
                ariaLabel: "View Extension Details",
              },
            ]}
          />
        </Stack.Item>
      </Stack>
      <TabList
        onTabSelect={onTabSelected}
        defaultSelectedValue={EXPERIMENTS_KEY}
      >
        <Tab id={EXPERIMENTS_KEY} value={EXPERIMENTS_KEY}>
          Experiments
        </Tab>
        <Tab id={ARCHIVED_KEY} value={ARCHIVED_KEY}>
          Archived
        </Tab>
      </TabList>
      {selectedTab === EXPERIMENTS_KEY && (
        <ExperimentsTable
          experiments={experiments}
          loadingExperiments={loadingExperiments}
          refresh={refresh}
          isRoot={isRoot}
        />
      )}
      {selectedTab === ARCHIVED_KEY && (
        <ArchiveTable
          items={archivedExperiments}
          refreshExperiments={refresh}
        />
      )}
    </Stack>
  );
};

export default ExperimentsTablePage;
